import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

const calcTimeLeft = (fechaFin: string): TimeLeft => {
  const diff = new Date(fechaFin).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: false,
  };
};

const CountdownBadge = ({ fechaFin }: { fechaFin: string }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft(fechaFin));

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(fechaFin)), 1000);
    return () => clearInterval(timer);
  }, [fechaFin]);

  if (timeLeft.expired) {
    return (
      <div className="flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-bold">
        <i className="ri-time-line"></i>
        Subasta Cerrada
      </div>
    );
  }

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 2;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold ${
      isUrgent ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-100 text-amber-800'
    }`}>
      <i className="ri-timer-line"></i>
      {timeLeft.days > 0 && <span>{timeLeft.days}d</span>}
      <span>{String(timeLeft.hours).padStart(2, '0')}h</span>
      <span>{String(timeLeft.minutes).padStart(2, '0')}m</span>
      <span>{String(timeLeft.seconds).padStart(2, '0')}s</span>
    </div>
  );
};

const SubastasPage = () => {
  const [subastas, setSubastas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'todas' | 'activas' | 'propiedades'>('todas');

  const fetchSubastas = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('products')
      .select('*')
      .eq('tipo_venta', 'subasta')
      .order('fecha_fin_subasta', { ascending: true });

    if (filtro === 'activas') {
      query = query.gt('fecha_fin_subasta', new Date().toISOString());
    } else if (filtro === 'propiedades') {
      query = query.eq('categoria', 'bienes_raices');
    }

    const { data } = await query;
    setSubastas(data || []);
    setLoading(false);
  }, [filtro]);

  useEffect(() => {
    fetchSubastas();
  }, [fetchSubastas]);

  const mockSubastas = [
    {
      id: 'mock-1',
      titulo: 'Toyota Tacoma 2020 TRD Off-Road',
      precio: 28000,
      precio_inicial: 22000,
      puja_actual: 26500,
      puja_count: 14,
      fecha_fin_subasta: new Date(Date.now() + 2 * 60 * 60 * 1000 + 35 * 60 * 1000).toISOString(),
      pueblo: 'Bayamón',
      categoria: 'autos',
      fotos: ['https://public.readdy.ai/ai/img_res/ed6e55220589f2cf0c16f6ca3d31085e.jpg'],
      tipo_venta: 'subasta',
    },
    {
      id: 'mock-2',
      titulo: 'Casa 3 Hab / 2 Baños — Urb. Villa del Rey, Caguas',
      precio: 185000,
      precio_inicial: 150000,
      puja_actual: 172000,
      puja_count: 8,
      fecha_fin_subasta: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000 + 4 * 60 * 60 * 1000).toISOString(),
      pueblo: 'Caguas',
      categoria: 'bienes_raices',
      fotos: ['https://readdy.ai/api/search-image?query=beautiful%20modern%20Puerto%20Rico%20house%20exterior%20white%20walls%20tropical%20garden%20palm%20trees%20blue%20sky%20residential%20property%20real%20estate%20photography%20wide%20angle&width=600&height=400&seq=sub-re-1&orientation=landscape'],
      tipo_venta: 'subasta',
    },
    {
      id: 'mock-3',
      titulo: 'MacBook Pro M3 16" 512GB — Como Nuevo',
      precio: 2400,
      precio_inicial: 1800,
      puja_actual: 2150,
      puja_count: 22,
      fecha_fin_subasta: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
      pueblo: 'San Juan',
      categoria: 'tecnologia',
      fotos: ['https://readdy.ai/api/search-image?query=MacBook%20Pro%20M3%20laptop%20open%20on%20clean%20white%20desk%20minimal%20setup%20professional%20product%20photography%20studio%20lighting%20silver%20aluminum&width=600&height=400&seq=sub-tech-1&orientation=landscape'],
      tipo_venta: 'subasta',
    },
    {
      id: 'mock-4',
      titulo: 'Solar 800 Cuerdas — Zona Rural, Utuado',
      precio: 320000,
      precio_inicial: 280000,
      puja_actual: 295000,
      puja_count: 5,
      fecha_fin_subasta: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      pueblo: 'Utuado',
      categoria: 'bienes_raices',
      fotos: ['https://readdy.ai/api/search-image?query=aerial%20view%20lush%20green%20Puerto%20Rico%20mountain%20land%20property%20tropical%20forest%20hills%20panoramic%20landscape%20real%20estate%20drone%20photography&width=600&height=400&seq=sub-re-2&orientation=landscape'],
      tipo_venta: 'subasta',
    },
    {
      id: 'mock-5',
      titulo: 'Hyundai Tucson 2022 — 18,000 millas',
      precio: 26000,
      precio_inicial: 21000,
      puja_actual: 24800,
      puja_count: 11,
      fecha_fin_subasta: new Date(Date.now() + 6 * 60 * 60 * 1000 + 20 * 60 * 1000).toISOString(),
      pueblo: 'Ponce',
      categoria: 'autos',
      fotos: ['https://readdy.ai/api/search-image?query=Hyundai%20Tucson%202022%20white%20SUV%20parked%20clean%20urban%20background%20professional%20car%20photography%20side%20view%20showroom%20quality&width=600&height=400&seq=sub-auto-2&orientation=landscape'],
      tipo_venta: 'subasta',
    },
    {
      id: 'mock-6',
      titulo: 'Apartamento 2/1 — Condado, San Juan',
      precio: 245000,
      precio_inicial: 200000,
      puja_actual: 231000,
      puja_count: 17,
      fecha_fin_subasta: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000).toISOString(),
      pueblo: 'San Juan',
      categoria: 'bienes_raices',
      fotos: ['https://readdy.ai/api/search-image?query=modern%20luxury%20apartment%20interior%20living%20room%20ocean%20view%20Condado%20San%20Juan%20Puerto%20Rico%20contemporary%20design%20bright%20open%20space&width=600&height=400&seq=sub-re-3&orientation=landscape'],
      tipo_venta: 'subasta',
    },
  ];

  const displayData = subastas.length > 0 ? subastas : mockSubastas;
  const filtered = filtro === 'propiedades'
    ? displayData.filter(s => s.categoria === 'bienes_raices')
    : filtro === 'activas'
    ? displayData.filter(s => new Date(s.fecha_fin_subasta) > new Date())
    : displayData;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=dramatic%20auction%20gavel%20hammer%20falling%20on%20dark%20elegant%20background%20golden%20light%20rays%20luxury%20marketplace%20high%20value%20items%20premium%20bidding%20event%20professional%20photography&width=1920&height=500&seq=subastas-hero&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 text-amber-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
              <i className="ri-auction-line text-base"></i>
              Marketplace de Alto Valor
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 leading-tight">
              Subastas en<br />
              <span className="text-amber-400">Tiempo Real</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              Pujas en vivo sobre autos, propiedades y artículos de alto valor. Gana al mejor precio.
            </p>
            <div className="flex flex-wrap gap-6">
              {[
                { icon: 'ri-hammer-line', label: 'Subastas Activas', value: `${filtered.filter(s => new Date(s.fecha_fin_subasta) > new Date()).length}` },
                { icon: 'ri-user-line', label: 'Pujadores Hoy', value: '47' },
                { icon: 'ri-money-dollar-circle-line', label: 'Valor Total', value: '$1.2M+' },
              ].map(stat => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className={`${stat.icon} text-xl text-amber-400`}></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-gray-400">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <span className="text-sm font-semibold text-gray-600">Filtrar:</span>
          {([
            { id: 'todas', label: 'Todas las Subastas', icon: 'ri-apps-line' },
            { id: 'activas', label: 'Solo Activas', icon: 'ri-live-line' },
            { id: 'propiedades', label: 'Bienes Raíces', icon: 'ri-building-2-line' },
          ] as { id: 'todas' | 'activas' | 'propiedades'; label: string; icon: string }[]).map(f => (
            <button
              key={f.id}
              onClick={() => setFiltro(f.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                filtro === f.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
              }`}
            >
              <i className={f.icon}></i>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((subasta) => {
              const isExpired = new Date(subasta.fecha_fin_subasta) <= new Date();
              const pujaActual = subasta.puja_actual || subasta.precio_inicial || subasta.precio;
              const isRealEstate = subasta.categoria === 'bienes_raices';

              return (
                <Link
                  key={subasta.id}
                  to={`/producto/${subasta.id}`}
                  className={`bg-white rounded-2xl overflow-hidden border-2 transition-all hover:-translate-y-1 cursor-pointer ${
                    isExpired ? 'border-gray-200 opacity-70' : 'border-gray-100 hover:border-amber-400'
                  }`}
                >
                  <div className="relative w-full h-52">
                    <img
                      src={subasta.fotos?.[0] || 'https://readdy.ai/api/search-image?query=auction%20item%20placeholder%20clean%20white%20background%20professional%20product%20photography&width=600&height=400&seq=sub-placeholder&orientation=landscape'}
                      alt={subasta.titulo}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                    {/* Badges top */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                      <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-auction-line"></i>
                        SUBASTA
                      </span>
                      {isRealEstate && (
                        <span className="bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-building-2-line"></i>
                          Propiedad
                        </span>
                      )}
                    </div>

                    {/* Countdown bottom */}
                    <div className="absolute bottom-3 left-3">
                      <CountdownBadge fechaFin={subasta.fecha_fin_subasta} />
                    </div>

                    {/* Puja count */}
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-user-line"></i>
                      {subasta.puja_count || 0} pujas
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 text-sm mb-3 line-clamp-2 leading-snug">
                      {subasta.titulo}
                    </h3>

                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                      <i className="ri-map-pin-line"></i>
                      {subasta.pueblo}
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500 font-medium">Puja Actual</span>
                        <span className="text-xs text-gray-400">Inicio: ${(subasta.precio_inicial || subasta.precio).toLocaleString()}</span>
                      </div>
                      <p className="text-2xl font-extrabold text-amber-600">
                        ${pujaActual.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        isExpired ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'
                      }`}>
                        {isExpired ? 'Cerrada' : 'Activa'}
                      </span>
                      <span className="text-xs font-bold text-gray-900 flex items-center gap-1">
                        Ver Subasta
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <i className="ri-auction-line text-4xl text-amber-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No hay subastas en este filtro</h3>
            <p className="text-gray-500 text-sm">Prueba otro filtro o vuelve más tarde</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SubastasPage;
